"use client";

import * as React from "react";
import { type DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import { ArrowLeft, Search } from "lucide-react";

import { cn } from "../../lib/utils";
import { Dialog, DialogContent } from "./dialog";
import { Text, type TextProps } from "./text";

/** Styled wrapper around the cmdk `Command` root. Forwards ref to the underlying element. */
const Command = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
	<CommandPrimitive
		ref={ref}
		className={cn(
			"flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-neutral-700",
			className,
		)}
		{...props}
	/>
));
Command.displayName = CommandPrimitive.displayName;

/**
 * Props for {@link CommandDialog}.
 *
 * Dialog-specific props (`open`, `defaultOpen`, `onOpenChange`, `modal`) are forwarded
 * to the underlying `Dialog`; remaining props are forwarded to the inner `Command`.
 */
type CommandDialogProps = Omit<DialogProps, "children"> &
	React.ComponentPropsWithoutRef<typeof CommandPrimitive>;

/** Command palette rendered inside a modal `Dialog`. */
const CommandDialog = ({
	children,
	open,
	defaultOpen,
	onOpenChange,
	modal,
	...props
}: CommandDialogProps) => (
	<Dialog
		open={open}
		defaultOpen={defaultOpen}
		onOpenChange={onOpenChange}
		modal={modal}
	>
		<DialogContent className="w-[600px] max-w-none overflow-hidden p-0 shadow-lg border-none">
			<Command
				className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-neutral-500 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
				{...props}
			>
				{children}
			</Command>
		</DialogContent>
	</Dialog>
);

/**
 * Props for {@link CommandHeader}.
 *
 * @property onBack - When provided, renders a back-arrow button that invokes this callback.
 * @property children - Header label content.
 */
type CommandHeaderProps = {
	onBack?: () => void;
	children: React.ReactNode;
};

const CommandHeader = ({ onBack, children }: CommandHeaderProps) => (
	<div className="flex items-center space-x-3 px-3 py-3 border-b border-neutral-200">
		{onBack && (
			<button
				type="button"
				className="flex items-center text-neutral-500 hover:text-neutral-700 focus:outline-none"
				onClick={onBack}
			>
				<ArrowLeft className="h-4 w-4" />
			</button>
		)}
		<span className="text-base font-semibold text-neutral-600">{children}</span>
	</div>
);

const CommandInput = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Input>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
	<div
		className="flex items-center border-b border-neutral-200 px-3"
		cmdk-input-wrapper=""
	>
		<Search className="mr-2 h-4 w-4 opacity-50" />
		<CommandPrimitive.Input
			ref={ref}
			className={cn(
				"flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none",
				className,
			)}
			{...props}
		/>
	</div>
));
CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.List
		ref={ref}
		className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
		{...props}
	/>
));
CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Empty>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Empty
		ref={ref}
		className={cn("py-20 text-center text-sm", className)}
		{...props}
	/>
));
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandLoading = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Loading>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Loading
		ref={ref}
		className={cn("py-20 text-center text-sm", className)}
		{...props}
	/>
));
CommandLoading.displayName = CommandPrimitive.Loading.displayName;

const CommandGroup = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Group>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Group
		ref={ref}
		className={cn(
			"overflow-hidden p-1 text-neutral-950 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-neutral-500",
			className,
		)}
		{...props}
	/>
));
CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Separator
		ref={ref}
		className={cn("-mx-1 h-px bg-neutral-200", className)}
		{...props}
	/>
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Item
		ref={ref}
		className={cn(
			"relative flex cursor-default select-none text-sm items-center rounded-sm px-2 py-1.5 outline-none aria-selected:bg-neutral-100 aria-selected:text-neutral-900 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
			className,
		)}
		{...props}
	/>
));
CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: TextProps<"span">) => (
	<Text
		as="span"
		variant="body-xs"
		className={cn("ml-auto text-neutral-500 dark:text-neutral-400", className)}
		{...props}
	/>
);
CommandShortcut.displayName = "CommandShortcut";

export {
	Command,
	CommandDialog,
	CommandHeader,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandLoading,
	CommandGroup,
	CommandItem,
	CommandShortcut,
	CommandSeparator,
};
